import clsx from 'clsx'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import * as Yup from 'yup'
import {CSSTransition} from 'react-transition-group'
import {
  Button,
  ButtonBack,
  InputTextFormik,
  SelectFormik,
} from '../../../../../../../_gori/partials/widgets'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import UseYupValidate from '../../../../../../../_gori/hooks/UseYupValidate'
import {useFormik} from 'formik'
import {OrdersConfig, OrderType} from '../../../../../orders/core/_const'
import {StoresListing} from '../StoresListing'
import {SettingsConfig} from '../../../../core/_const'
import SettingsService from '../../../../core/_requests'
import {toast} from 'react-toastify'
import {FromOrToModal} from '../../../../../orders'
import {isEmpty, omit} from 'lodash'
import {PromotionBanner, BannerContent} from '../../../../../common/components/PromotionBanner'
import {usePromoEligibility} from '../../../../../../../_gori/hooks/UsePromoEligibility'
import TemuSideBanner from '../../../../../promotions/TemuSideBanner'
import {StorageHelpers, KTSVG} from '../../../../../../../_gori/helpers'
import {ConnectionLink} from '../ConnectionLink'
const ConnectTemu: React.FC = () => {
  const [, setIsLoading] = useState<boolean>(false)
  const intl = useIntl()
  const navigate = useNavigate()
  const [isReload, setIsReload] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const {getMarketPromoStatus} = usePromoEligibility()
  const temuPromoStatus = getMarketPromoStatus('temu')
  const isPromoEligible = temuPromoStatus.isEligible
  const isPromoApplied = temuPromoStatus.isApplied

  // Temporarily hidden due to fraud — change back to true to re-enable
  const temuBannerVisible = false

  const [showModal, setShowModal] = useState<{shippingAddress: boolean}>({
    shippingAddress: false,
  })

  const {temuYup, stringYup, orderYup, regexYup, infoYup} = UseYupValidate()
  const validationSchema = Yup.object().shape({
    seller_type: temuYup.sellerType,
    store_name: temuYup.storeName,
    access_token: temuYup.accessToken,
    region: temuYup.region,
  })

  const initialValues = useMemo(() => {
    const init: any = Object.entries(OrdersConfig.SHIPPING).reduce(
      (initValue, [key, value]) => {
        switch (value.value) {
          case OrdersConfig.SHIPPING.RESIDENTIAL.value:
            initValue.from[`from_${value.value}`] = false
            break
          case OrdersConfig.SHIPPING.VERIFIED.value:
          case OrdersConfig.SHIPPING.SAVE_ADDRESS.value:
            break
          default:
            initValue.from[`from_${value.value}`] = ''
            break
        }
        return initValue
      },
      {from: {}}
    )
    return {
      ...init,
      store_type: OrderType.TEMU,
      seller_type: '',
      store_name: '',
      access_token: '',
      region: {
        url: '',
        code: '',
      },
    }
  }, [])

  const onSubmit = async (values, {setStatus}) => {
    setIsLoading(true)
    setStatus('')
    try {
      let region_code = values.region?.code?.length > 0 ? formik.values.region.code : 'us'
      let payload = omit({...values, region_code}, 'region')

      const res = await SettingsService.connectTemuStore(payload)
      if (res) {
        StorageHelpers.setItemLocalStorage('cardstore', {hidden: true})
        window.dispatchEvent(new Event('storage'))
        toast.success(intl.formatMessage({id: 'CONNECTION_SUCCESSFUL'}))
        formik.resetForm()
      }
    } catch (error: any) {
      let message = error?.response?.data?.error?.message ?? ''
      setStatus(isEmpty(message) ? intl.formatMessage({id: 'INVALID_API_KEY'}) : message)
    } finally {
      setIsLoading(false)
      setIsReload(true)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  const temuBannerContent: Partial<BannerContent> = {
    helpLinkTo: '/help-center/store-connection/temu',
  }

  const goBack = () => {
    navigate('/settings/connections')
  }

  useEffect(() => {
    formik.validateForm()

    if (formik.isValid && !formik.isSubmitting) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.isValid, formik.isSubmitting])

  const validateAddress = useCallback(
    (name) => {
      return {
        [`${name}_company`]: Yup.string().when([`${name}_first_name`], {
          is: (first_name) => !first_name || first_name.trim() === '',
          then: orderYup.companyFirstName('COMPANY'),
          otherwise: stringYup(255, 'COMPANY', false),
        }),
        [`${name}_first_name`]: Yup.string().when(`${name}_company`, {
          is: (company) => !company || company.trim() === '',
          then: orderYup.companyFirstName('FIRST_NAME'),
          otherwise: stringYup(255, 'FIRST_NAME', false),
        }),
        [`${name}_street1`]: stringYup(255, 'ADDRESS'),
        [`${name}_city`]: stringYup(255, 'CITY'),
        [`${name}_state`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.state(),
          otherwise: stringYup(255, 'STATE'),
        }),
        [`${name}_country`]: regexYup.country(),
        [`${name}_zip`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.zipCode(),
          otherwise: stringYup(255, 'POSTAL_CODE'),
        }),
        [`${name}_last_name`]: stringYup(255, 'LAST_NAME', false),
        [`${name}_street2`]: stringYup(255, 'ADDRESS_2', false),
        [`${name}_phone`]: infoYup.phone('PHONE', false),
        [`${name}_email`]: infoYup.email('EMAIL', false),
      }
    },
    [orderYup, regexYup, stringYup, infoYup]
  )

  return (
    <>
      {showModal.shippingAddress && (
        <FromOrToModal
          data={{
            show: showModal.shippingAddress,
            name: 'from',
            data: formik.getFieldProps('from').value,
            validate: validateAddress('from'),
          }}
          labelModal={
            intl.formatMessage({id: 'TEMU'}) + ' ' + intl.formatMessage({id: 'SHIP_FROM_LOCATION'})
          }
          handleSave={(name, data) => {
            formik.setValues({...formik.values, ...data})
            formik.submitForm()
            setShowModal((prev) => ({...prev, shippingAddress: false}))
          }}
          handleClose={() => {
            setShowModal((prev) => ({...prev, shippingAddress: false}))
          }}
        />
      )}
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='store-connect-page'>
          <div className='store-connect-page__header'>
            <div className='store-connect-page__header-main'>
              <div className='d-flex align-items-center store-connect-page__back'>
                <ButtonBack
                  className='store-connect-page__back-button me-0'
                  label={intl.formatMessage({id: 'BACK'})}
                  event={goBack}
                />
                <button
                  type='button'
                  className='store-connect-page__back-arrow'
                  aria-label='Go back'
                  onClick={goBack}
                >
                  <KTSVG path='/media/gori/left_arrow.svg' />
                </button>
              </div>
              <div className='d-flex flex-column'>
                <div className='d-flex gap-4 align-items-center flex-wrap'>
                  <span className='store-connect-page__title fw-bolder text-dark fs-3'>
                    {intl.formatMessage({id: 'CONNECT_TO'})} {intl.formatMessage({id: 'TEMU'})}
                  </span>
                </div>
                <ConnectionLink url='/help-center/store-connection/temu' />
              </div>
            </div>
            {isPromoApplied && (
              <PromotionBanner
                isEligible={isPromoEligible}
                isApplied={isPromoApplied}
                content={temuBannerContent}
              />
            )}
          </div>

          <div className='store-connect-page__body'>
            <div className='row'>
              <div
                className={`col-12 col-lg-${temuBannerVisible ? 9 : 12} ${
                  temuBannerVisible ? 'order-2 order-lg-1' : ''
                }`}
              >
                <div className='store-connect-page__form-card store-connect-page__form-card--stacked'>
                  <div className='store-connect-page__form-stack'>
                    <div className='row align-items-center store-connect-page__form-stack-row'>
                      <SelectFormik
                        className='store-connect-page__form-stack-input col-10'
                        labelClassName='store-connect-page__form-stack-label col-2 col-form-label text-nowrap'
                        label={'Temu ' + intl.formatMessage({id: 'SELLER_TYPE'})}
                        required
                        options={SettingsConfig.TEMU.TYPE}
                        hasUseIntl={true}
                        formik={formik}
                        name={'seller_type'}
                        emptyDefault={false}
                      />
                    </div>
                    <div className='row align-items-center store-connect-page__form-stack-row'>
                      <InputTextFormik
                        className='store-connect-page__form-stack-input col-10'
                        labelClassName='store-connect-page__form-stack-label col-2 col-form-label text-nowrap'
                        required
                        label={'Temu ' + intl.formatMessage({id: 'STORE_NAME'})}
                        formik={formik}
                        name={'store_name'}
                      />
                    </div>
                    <div className='row align-items-center store-connect-page__form-stack-row'>
                      <InputTextFormik
                        className='store-connect-page__form-stack-input col-10'
                        labelClassName='store-connect-page__form-stack-label col-2 col-form-label text-nowrap'
                        required
                        label={'Temu ' + intl.formatMessage({id: 'ACCESS_TOKEN'})}
                        formik={formik}
                        name={'access_token'}
                      />
                    </div>
                    <div className='row align-items-center store-connect-page__form-stack-row'>
                      <SelectFormik
                        className='store-connect-page__form-stack-input col-10'
                        labelClassName='store-connect-page__form-stack-label col-2 col-form-label text-nowrap'
                        label={'Temu ' + intl.formatMessage({id: 'REGION'})}
                        required
                        options={SettingsConfig.TEMU.REGION}
                        hasUseIntl={true}
                        formik={formik}
                        name={'region'}
                        emptyDefault={false}
                      />
                    </div>
                    {formik.status && (
                      <div className='col-md-10 mt-5 alert alert-danger'>
                        <div className='alert-text font-weight-bold'>{formik.status}</div>
                      </div>
                    )}
                    <div className='store-connect-page__form-actions text-end'>
                      <Button
                        className='btn btn-advanced-primary mt-7 store-connect-page__form-stack-connect'
                        label={intl.formatMessage({id: 'CONNECT'})}
                        loadingText={intl.formatMessage({id: 'CONNECT'})}
                        loading={formik.isSubmitting}
                        disabled={isDisabled}
                        event={() => {
                          setShowModal((prev) => ({...prev, shippingAddress: true}))
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={clsx('store-connect-page__stores', {
                    'pe-lg-3 pe-xl-4': temuBannerVisible,
                  })}
                  style={temuBannerVisible ? {maxWidth: 'calc(100% - 50px)'} : undefined}
                >
                  <StoresListing
                    isReload={isReload}
                    setIsReload={setIsReload}
                    storeType={OrderType.TEMU}
                  />
                </div>
              </div>
              {temuBannerVisible && (
                <div className='col-12 col-lg-3 order-1 order-lg-2 mb-4 mb-lg-0 d-flex justify-content-center justify-content-lg-end align-items-start'>
                  <TemuSideBanner />
                </div>
              )}
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}

export {ConnectTemu}
